import React from "react";
import { createUseStyles, useTheme } from "react-jss";
import classnames from "classnames";
import { useSelector } from "react-redux";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { MentionModel } from "../../../models/MentionModel";
import NetPromoterItemRenderer from "./NetPromoterItemRenderer";
import useStyle from "./NetPromoterRenderer.styles";
import useQuestion from "../../../hooks/useQuestion";
import useDimensionContext from "../../../hooks/useDimensionContext";
import useTranslatable from "../../../hooks/useTranslatable";
import OpenEndRenderer from "./OpenEndRenderer";
import { QuestionLabel } from "./QuestionRenderer";
import { isNullOrEmpty } from "../../../utils";
import {
    getQuestionTitleAndLabelAriaLabelledByIds,
    stripHtmlForAccessibleName,
} from "../../../utils/WCAGHelper";
import { translate } from "../../../models/utilities/translationUtilities";
import { StoreState } from "../../../models/states/StoreState";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import { Theme } from "../../../types/Theme";

interface Props extends RendererProps {
    mentions: MentionModel[];
    choices: ChoiceModel[];
    onSelect(choice: ChoiceModel): void;
    isDisabled?: boolean;
    showFeedbackQuestion?: boolean;
    feedbackQuestion?: string;
    feedbackVariable?: string;
    openEndValue?: string;
    onOpenEndChange?(event: any): void;
    onOpenEndBlur?(): void;
}

function isSelected(props: Props, choice: ChoiceModel) {
    return props.mentions.some((mention) => mention.value === choice.value);
}

/**
 * Question body copy lives on the radiogroup (aria-labelledby _LABEL when present). Each radio only
 * references the scale value id plus optional NPS left/right anchors on the endpoints.
 */
function getNetPromoterChoiceAriaLabelledBy(
    questionElementId: string | undefined,
    choice: ChoiceModel,
    choiceIndex: number,
    choiceCount: number,
    hasLeftLabelText: boolean,
    hasRightLabelText: boolean,
): string | undefined {
    if (!questionElementId) {
        return undefined;
    }
    const valueId = `${choice.elementId}_NPS_VALUE`;
    const leftId = `${questionElementId}_NPS_LEFTLABEL`;
    const rightId = `${questionElementId}_NPS_RIGHTLABEL`;
    const ids: string[] = [];
    if (choiceIndex === 0 && hasLeftLabelText) {
        ids.push(leftId);
    }
    ids.push(valueId);
    if (choiceIndex === choiceCount - 1 && hasRightLabelText) {
        ids.push(rightId);
    }
    return ids.join(" ");
}

export default function NetPromoterRenderer(props: Props) {
    const theme = useTheme() as Theme;
    const useModernTheme = shouldUseModernTheme(theme);
    const classes = useStyle({ theme });
    const {
        choices,
        gridRendering,
        variable,
        onSelect,
        isDisabled = false,
        showFeedbackQuestion,
        feedbackQuestion,
        feedbackVariable,
        openEndValue,
        onOpenEndChange,
        onOpenEndBlur,
        questionnaire,
        language,
    } = props;
    const question = useQuestion();
    const hasMainVariableValidationError = useSelector((state: StoreState) => {
        const name = variable?.name;
        if (!name) {
            return false;
        }
        const errs = state.interview.validation?.errorsByVariableName?.[name];
        return (errs?.length ?? 0) > 0;
    });
    const incompleteAnswerHint = stripHtmlForAccessibleName(
        translate(questionnaire ?? question, "IncompleteAnswer", language) || "",
    );
    const leftLabel = useTranslatable(question, "LeftLabel");
    const rightLabel = useTranslatable(question, "RightLabel");
    const displayQuestion = useTranslatable(question, feedbackQuestion);
    const dimension = useDimensionContext();
    const isMobileDimension = dimension && dimension.maxWidth(480);
    const questionElementId = question?.elementId;
    const hasLeftLabelText = !isNullOrEmpty(stripHtmlForAccessibleName(leftLabel ?? ""));
    const hasRightLabelText = !isNullOrEmpty(stripHtmlForAccessibleName(rightLabel ?? ""));
    const leftLabelId = questionElementId && hasLeftLabelText ? `${questionElementId}_NPS_LEFTLABEL` : undefined;
    const rightLabelId = questionElementId && hasRightLabelText ? `${questionElementId}_NPS_RIGHTLABEL` : undefined;

    return (
        <div>
            {hasMainVariableValidationError && (
                <button
                    type="button"
                    className={classnames(classes.validationFocusAnchor, "allowFocusOnQuestion")}
                    aria-label={incompleteAnswerHint || "Incomplete answer"}
                />
            )}
            {(leftLabel !== null || rightLabel !== null) && isMobileDimension && (
                <div
                    id={leftLabelId}
                    className={classnames(
                        classes.label,
                        classes.mobileLabel,
                        isMobileDimension && classes.dynamicMobileLabel,
                    )}
                    dangerouslySetInnerHTML={{ __html: leftLabel }}
                />
            )}
            <div
                className={classnames(classes.choices, isMobileDimension && classes.dynamicChoices)}
                role="radiogroup"
                aria-disabled={isDisabled || undefined}
                aria-invalid={
                    useModernTheme && hasMainVariableValidationError ? true : undefined
                }
                {...(questionElementId
                    ? {
                          // DEV-98353: name the radiogroup with TITLE + LABEL, not one or the
                          // other — a question with both a title and body text was previously
                          // announcing only the body.
                          "aria-labelledby": getQuestionTitleAndLabelAriaLabelledByIds(
                              question,
                              language,
                          ),
                      }
                    : {})}
            >
                {choices.map((choice, choiceIndex) => {
                    const selected = isSelected(props, choice);
                    const ariaLabelledBy = getNetPromoterChoiceAriaLabelledBy(
                        questionElementId,
                        choice,
                        choiceIndex,
                        choices.length,
                        hasLeftLabelText,
                        hasRightLabelText,
                    );
                    return (
                        <NetPromoterItemRenderer
                            key={choice.elementId}
                            {...getRendererProps(props)}
                            gridRendering={gridRendering}
                            selected={selected}
                            isDisabled={isDisabled}
                            choice={choice}
                            variable={variable}
                            onSelect={onSelect}
                            ariaLabelledBy={ariaLabelledBy}
                        />
                    );
                })}
            </div>
            {(leftLabel !== null || rightLabel !== null) && (
                <>
                    {!isMobileDimension && (
                        <div
                            className={classnames(
                                classes.labels,
                                isMobileDimension && classes.dynamicLabels,
                            )}
                        >
                            <div
                                id={leftLabelId}
                                className={classes.label}
                                dangerouslySetInnerHTML={{ __html: leftLabel }}
                            />
                            <div
                                id={rightLabelId}
                                className={classes.label}
                                dangerouslySetInnerHTML={{ __html: rightLabel }}
                            />
                        </div>
                    )}

                    {isMobileDimension && (
                        <div
                            id={rightLabelId}
                            className={classnames(
                                classes.label,
                                classes.mobileLabel,
                                isMobileDimension && classes.dynamicMobileLabel,
                            )}
                            dangerouslySetInnerHTML={{ __html: rightLabel }}
                        />
                    )}
                </>
            )}
            {showFeedbackQuestion && (
                <>
                    <QuestionLabel
                        question={question}
                        text={displayQuestion}
                        shortText={undefined}
                        classes={createUseStyles(undefined, undefined)}
                        elementNameOrId={feedbackVariable}
                    />
                    <OpenEndRenderer
                        {...getRendererProps(props)}
                        name={feedbackVariable}
                        label={displayQuestion}
                        value={openEndValue}
                        isDisabled={false}
                        onOpenEndBlur={onOpenEndBlur}
                        onOpenEndChange={onOpenEndChange}
                    />
                </>
            )}
        </div>
    );
}
